import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { Input, setComponentTemplate } from "@ember/component";
import { action } from "@ember/object";
import { service } from "@ember/service";
import DButton from "discourse/components/d-button";
import UserLink from "discourse/components/user-link";
import { ajax } from "discourse/lib/ajax";
import { extractError, popupAjaxError } from "discourse/lib/ajax-error";
import { getPasskeyCredential, isWebauthnSupported } from "discourse/lib/webauthn";
import I18n from "discourse-i18n";
import { createTemplateFactory } from "@ember/template-factory";
export default class ConfirmSession extends Component {
  static #_ = (() => dt7948.g(this.prototype, "dialog", [service]))();
  #dialog = (() => (dt7948.i(this, "dialog"), void 0))();
  static #_2 = (() => dt7948.g(this.prototype, "currentUser", [service]))();
  #currentUser = (() => (dt7948.i(this, "currentUser"), void 0))();
  static #_3 = (() => dt7948.g(this.prototype, "siteSettings", [service]))();
  #siteSettings = (() => (dt7948.i(this, "siteSettings"), void 0))();
  static #_4 = (() => dt7948.g(this.prototype, "errorMessage", [tracked]))();
  #errorMessage = (() => (dt7948.i(this, "errorMessage"), void 0))();
  static #_5 = (() => dt7948.g(this.prototype, "resetEmailSent", [tracked], function () {
    return null;
  }))();
  #resetEmailSent = (() => (dt7948.i(this, "resetEmailSent"), void 0))();
  passwordLabel = (() => I18n.t("user.password.title"))();
  instructions = (() => I18n.t("user.confirm_access.instructions"))();
  loggedInAs = (() => I18n.t("user.confirm_access.logged_in_as"))();
  finePrint = (() => I18n.t("user.confirm_access.fine_print"))();
  get canUsePasskeys() {
    return this.siteSettings.enable_local_logins && this.siteSettings.enable_passkeys && this.currentUser.user_passkeys?.length > 0 && isWebauthnSupported();
  }
  async confirmWithPasskey() {
    try {
      const publicKeyCredential1 = await getPasskeyCredential(e1 => this.dialog.alert(e1));
      if (!publicKeyCredential1) {
        return;
      }
      const result1 = await ajax("/u/confirm-session.json", {
        type: "POST",
        data: {
          publicKeyCredential: publicKeyCredential1
        }
      });
      if (result1.success) {
        this.errorMessage = null;
        this.dialog.didConfirmWrapped();
      } else {
        this.errorMessage = I18n.t("user.confirm_access.incorrect_passkey");
      }
    } catch (e1) {
      popupAjaxError(e1);
    }
  }
  static #_6 = (() => dt7948.n(this.prototype, "confirmWithPasskey", [action]))();
  async submit() {
    this.errorMessage = this.password ? null : I18n.t("user.confirm_access.incorrect_password");
    const result1 = await ajax("/u/confirm-session.json", {
      type: "POST",
      data: {
        password: this.password
      }
    });
    if (result1.success) {
      this.errorMessage = null;
      this.dialog.didConfirmWrapped();
    } else {
      this.errorMessage = I18n.t("user.confirm_access.incorrect_password");
    }
  }
  static #_7 = (() => dt7948.n(this.prototype, "submit", [action]))();
  async sendPasswordResetEmail() {
    try {
      const result1 = await ajax("/session/forgot_password.json", {
        data: {
          login: this.currentUser.username
        },
        type: "POST"
      });
      if (result1.success) {
        this.errorMessage = null;
        this.resetEmailSent = I18n.t("user.confirm_access.password_reset_email_sent");
      } else {
        this.errorMessage = I18n.t("user.confirm_access.cannot_send_password_reset_email");
      }
    } catch (e1) {
      this.errorMessage = extractError(e1, I18n.t("user.confirm_access.cannot_send_password_reset_email"));
    }
  }
  static #_8 = (() => dt7948.n(this.prototype, "sendPasswordResetEmail", [action]))();
  static #_9 = (() => setComponentTemplate(createTemplateFactory(
  /*
    
      {{#if this.errorMessage}}
        <div class="alert alert-error">
          {{this.errorMessage}}
        </div>
      {{/if}}
  
      <div class="control-group confirm-session">
        <div class="confirm-session__instructions">
          {{this.instructions}}
        </div>
  
        <div class="confirm-session__instructions">
          <span>{{this.loggedInAs}}</span>
          <UserLink @user={{this.currentUser}}>
            {{this.currentUser.username}}
          </UserLink>
        </div>
  
        <form>
          <label class="control-label">{{this.passwordLabel}}</label>
          <div class="controls">
            <div class="inline-form">
              <Input
                @value={{this.password}}
                @type="password"
                id="password"
                class="input-large"
                autofocus="autofocus"
              />
              <DButton
                class="btn-primary"
                @type="submit"
                @action={{this.submit}}
                @label="user.password.confirm"
              />
            </div>
            <div class="confirm-session__reset">
              <DButton
                @label="user.confirm_access.forgot_password"
                @action={{this.sendPasswordResetEmail}}
                @class="btn-link btn-flat confirm-session__reset-btn"
              />
              {{#if this.resetEmailSent}}
                <span class="confirm-session__reset-email-sent">
                  {{this.resetEmailSent}}
                </span>
              {{/if}}
            </div>
            {{#if this.canUsePasskeys}}
              <div class="confirm-session__passkey">
                <DButton
                  @action={{this.confirmWithPasskey}}
                  @label="user.passkeys.confirm_button"
                  @icon="user"
                />
              </div>
            {{/if}}
          </div>
        </form>
  
        <div class="confirm-session__fine-print">
          {{this.finePrint}}
        </div>
  
      </div>
    
  */
  {
    "id": "xHL8HopW",
    "block": "[[[1,\"\\n\"],[41,[30,0,[\"errorMessage\"]],[[[1,\"      \"],[10,0],[14,0,\"alert alert-error\"],[12],[1,\"\\n        \"],[1,[30,0,[\"errorMessage\"]]],[1,\"\\n      \"],[13],[1,\"\\n\"]],[]],null],[1,\"\\n    \"],[10,0],[14,0,\"control-group confirm-session\"],[12],[1,\"\\n      \"],[10,0],[14,0,\"confirm-session__instructions\"],[12],[1,\"\\n        \"],[1,[30,0,[\"instructions\"]]],[1,\"\\n      \"],[13],[1,\"\\n\\n      \"],[10,0],[14,0,\"confirm-session__instructions\"],[12],[1,\"\\n        \"],[10,1],[12],[1,[30,0,[\"loggedInAs\"]]],[13],[1,\"\\n        \"],[8,[32,0],null,[[\"@user\"],[[30,0,[\"currentUser\"]]]],[[\"default\"],[[[[1,\"\\n          \"],[1,[30,0,[\"currentUser\",\"username\"]]],[1,\"\\n        \"]],[]]]]],[1,\"\\n      \"],[13],[1,\"\\n\\n      \"],[10,\"form\"],[12],[1,\"\\n        \"],[10,\"label\"],[14,0,\"control-label\"],[12],[1,[30,0,[\"passwordLabel\"]]],[13],[1,\"\\n        \"],[10,0],[14,0,\"controls\"],[12],[1,\"\\n          \"],[10,0],[14,0,\"inline-form\"],[12],[1,\"\\n            \"],[8,[32,1],[[24,1,\"password\"],[24,0,\"input-large\"],[24,\"autofocus\",\"autofocus\"]],[[\"@value\",\"@type\"],[[30,0,[\"password\"]],\"password\"]],null],[1,\"\\n            \"],[8,[32,2],[[24,0,\"btn-primary\"]],[[\"@type\",\"@action\",\"@label\"],[\"submit\",[30,0,[\"submit\"]],\"user.password.confirm\"]],null],[1,\"\\n          \"],[13],[1,\"\\n          \"],[10,0],[14,0,\"confirm-session__reset\"],[12],[1,\"\\n            \"],[8,[32,2],null,[[\"@label\",\"@action\",\"@class\"],[\"user.confirm_access.forgot_password\",[30,0,[\"sendPasswordResetEmail\"]],\"btn-link btn-flat confirm-session__reset-btn\"]],null],[1,\"\\n\"],[41,[30,0,[\"resetEmailSent\"]],[[[1,\"              \"],[10,1],[14,0,\"confirm-session__reset-email-sent\"],[12],[1,\"\\n                \"],[1,[30,0,[\"resetEmailSent\"]]],[1,\"\\n              \"],[13],[1,\"\\n\"]],[]],null],[1,\"          \"],[13],[1,\"\\n\"],[41,[30,0,[\"canUsePasskeys\"]],[[[1,\"            \"],[10,0],[14,0,\"confirm-session__passkey\"],[12],[1,\"\\n              \"],[8,[32,2],null,[[\"@action\",\"@label\",\"@icon\"],[[30,0,[\"confirmWithPasskey\"]],\"user.passkeys.confirm_button\",\"user\"]],null],[1,\"\\n            \"],[13],[1,\"\\n\"]],[]],null],[1,\"        \"],[13],[1,\"\\n      \"],[13],[1,\"\\n\\n      \"],[10,0],[14,0,\"confirm-session__fine-print\"],[12],[1,\"\\n        \"],[1,[30,0,[\"finePrint\"]]],[1,\"\\n      \"],[13],[1,\"\\n\\n    \"],[13],[1,\"\\n  \"]],[],false,[\"if\"]]",
    "moduleName": "/var/www/discourse/app/assets/javascripts/discourse/node_modules/.embroider/rewritten-app/components/dialog-messages/confirm-session.js",
    "scope": () => [UserLink, Input, DButton],
    "isStrictMode": true
  }), this))();
}